import React, { useCallback } from 'react';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
import styled from 'styled-components';
import { SkeletonItem } from '@helpcenter/skeleton-item';
import * as mixins from '@helpcenter/styles/src/mixins';
import type { WrappedComponentProps } from 'react-intl';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import Heading from '@atlaskit/heading';
import IconTile from '@atlaskit/icon/icon-tile';
import BookIcon from '@atlaskit/icon/core/book-with-bookmark';
import QuestionsIcon from '@atlaskit/icon/core/comment';
import HipchatLobbyIcon from '@atlaskit/icon/core/lobby-bell';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { UI_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { useExternalLinkIcon } from '../use-external-link-icon';
import messages from './messages';
import { iconDimension } from './styled';
import type { ExternalLinkObject } from 'rest/external-links/types';

const ISSUE_A11Y_37386 = 'issue-a11y-37386';
const HC_ADS_DROP_COMMIT_2 = 'hc_ads_drop_commit_2';

const isIssueA11y37386Enabled = (): boolean => FeatureGates.checkGate(ISSUE_A11Y_37386);

export type Props = ExternalLinkObject &
	WrappedComponentProps & { position?: number; isLoggedIn: boolean; isPageInEditMode: boolean };
export const ExternalLinkItem: React.FC<Props> = (props) => {
	const {
		title,
		description,
		resourceType,
		link,
		position,
		isLoggedIn,
		isPageInEditMode,
		intl: { formatMessage },
	} = props;

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data: iconUrl, isLoading: isIconLoading } = useExternalLinkIcon({ link, isLoggedIn });
	const isHcAdsDropCommit2Enabled = FeatureGates.checkGate(HC_ADS_DROP_COMMIT_2);

	const getIconLabel = (iconType: ExternalLinkObject['resourceType']) => {
		switch (iconType) {
			case 'request-form': {
				return formatMessage(messages.externalLinkRequestFormIconLabel);
			}
			case 'knowledge': {
				return formatMessage(messages.externalLinkKnowledgeIconLabel);
			}
			case 'channel':
			default: {
				return formatMessage(messages.externalLinkHelpChannelIconLabel);
			}
		}
	};

	const getFallbackIcon = (iconType: ExternalLinkObject['resourceType']) => {
		const iconLabel = isIssueA11y37386Enabled() ? '' : getIconLabel(iconType);

		switch (iconType) {
			case 'request-form': {
				return (
					<HipchatLobbyIcon
						spacing="spacious"
						label={iconLabel}
						testId="external-link-icon-request-form"
					/>
				);
			}
			case 'knowledge': {
				return (
					<BookIcon
						label={iconLabel}
						size="medium"
						spacing="compact"
						testId="external-link-icon-knowledge"
					/>
				);
			}
			case 'channel':
			default: {
				return (
					<QuestionsIcon spacing="spacious" label={iconLabel} testId="external-link-icon-channel" />
				);
			}
		}
	};

	const onLinkClick = useCallback(() => {
		if (isPageInEditMode) {
			return;
		}
		const analyticsEvent = createAnalyticsEvent({
			resourceType,
			analyticsType: UI_EVENT_TYPE,
			action: 'clicked',
			link_position: position,
		});

		analyticsEvent.fire();
	}, [createAnalyticsEvent, isPageInEditMode, position, resourceType]);

	const getIcon = () => {
		if (isIconLoading) {
			return <SkeletonItem height={iconDimension} width={iconDimension} />;
		}

		if (iconUrl) {
			return <img src={iconUrl} alt="" />;
		}

		return getFallbackIcon(resourceType);
	};
	const linkContent = (
		<a
			target="_blank"
			rel="noopener noreferrer"
			href={link}
			onClick={onLinkClick}
			tabIndex={isPageInEditMode ? -1 : undefined}
		>
			{isHcAdsDropCommit2Enabled ? (
				<IconTile size="small" icon={() => getIcon()} label="" appearance="gray" />
			) : (
				<IconWrapper>{getIcon()}</IconWrapper>
			)}
			<ContentWrapper>
				<Heading size="xsmall" as="h3">
					{title}
				</Heading>
				{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
				<p>{description}</p>
			</ContentWrapper>
		</a>
	);

	return (
		<LinkWrapper
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Disabled to rollout go/ui-styling-standard tooling, please resolve
			className="cv-exclude-from-customisation"
			isPageInEditMode={isPageInEditMode}
			useIconTile={isHcAdsDropCommit2Enabled}
		>
			{!isPageInEditMode ? (
				<Tooltip content={formatMessage(messages.externalLinkHoverTooltip)} position="mouse">
					{linkContent}
				</Tooltip>
			) : (
				linkContent
			)}
		</LinkWrapper>
	);
};

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const LinkWrapper = styled.li<{ isPageInEditMode: boolean; useIconTile: boolean }>`
	list-style: none;

	a {
		display: flex;
		cursor: pointer;
		padding: ${token('space.050', '4px')};
		gap: ${({ useIconTile }) => (useIconTile ? token('space.200', '16px') : undefined)};
		pointer-events: ${({ isPageInEditMode }) => {
			return isPageInEditMode ? 'none' : 'auto';
		}};

		&:hover,
		&:active,
		&:focus {
			text-decoration: none;
			background-color: ${token('color.background.neutral.subtle.hovered', '#FAFBFC')};
		}
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const ContentWrapper = styled.div`
	flex: 1;
	${mixins.multiLineClamp(4)};
	word-break: break-word;

	& p {
		margin-top: ${token('space.050', '4px')};
		color: ${token('color.text.subtlest', '#505F79')};
	}
`;

// eslint-disable-next-line @atlaskit/design-system/no-styled-tagged-template-expression, @atlaskit/ui-styling-standard/no-styled -- Disabled to rollout go/ui-styling-standard tooling, please resolve
const IconWrapper = styled.div`
	margin-right: ${token('space.200', '16px')};
	margin-top: ${token('space.050', '4px')};
	min-width: ${iconDimension};
	color: ${token('color.icon', '#172B4D')};

	& img {
		height: ${iconDimension};
		object-fit: contain;
	}
`;
